import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import {
  createChart,
  CrosshairMode,
  IChartApi,
  ISeriesApi,
  UTCTimestamp,
} from 'lightweight-charts';
import { Sparkline } from '../../../components/Sparkline.tsx';
import { LogoImage } from '../../../components/LogoImage.js';
import {
  polygonApi,
  type CompanyInfo,
  type HistoricalData,
  type StockQuote,
} from '../../../services/polygon.js';
import { getApiEndpoint } from '../../../config/api.js';

type MarketFooterStripProps = {
  symbol?: string | null;
  profileId?: string | null;
  articleTimestamp?: string | null;
  onNavigateToProfile?: (profileId?: string | null, symbol?: string | null) => void;
};

type CombinedEntry = Awaited<ReturnType<typeof polygonApi.getBatchCombinedData>>[number];

type PerformanceKey = '1D' | '5D' | '1M' | 'YTD' | '1Y';

type MarketDataState = {
  entry: CombinedEntry;
  candles: HistoricalData[];
  benchmark: HistoricalData[];
};

type MetricDescriptor = {
  label: string;
  value: string;
  helper?: string;
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const compactNumberFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

const PERFORMANCE_OPTIONS: Array<{ id: PerformanceKey; label: string }> = [
  { id: '1D', label: '1D' },
  { id: '5D', label: '5D' },
  { id: '1M', label: '1M' },
  { id: 'YTD', label: 'YTD' },
  { id: '1Y', label: '1Y' },
];

const formatCurrency = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return currencyFormatter.format(value);
};

const formatPercent = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return `${value >= 0 ? '+' : ''}${percentFormatter.format(value / 100)}`;
};

const formatPercentRaw = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const formatCompact = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) {
    return '—';
  }
  return compactNumberFormatter.format(value);
};

const formatInteger = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return integerFormatter.format(value);
};

const toDateKey = (timestamp: number) => new Date(timestamp).toISOString().split('T')[0];

const deriveSession = (reference?: Date) => {
  const now = reference ? new Date(reference) : new Date();
  const easternString = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
  const et = new Date(easternString);
  const day = et.getDay();
  if (day === 0 || day === 6) {
    return 'after-hours';
  }
  const minutes = et.getHours() * 60 + et.getMinutes();
  if (minutes >= 9 * 60 + 30 && minutes < 16 * 60) {
    return 'regular';
  }
  if (minutes >= 4 * 60 && minutes < 9 * 60 + 30) {
    return 'pre-market';
  }
  return 'after-hours';
};

const useMarketData = (symbol?: string | null): [MarketDataState | null, boolean, string | null] => {
  const normalizedSymbol = React.useMemo(() => symbol?.trim().toUpperCase() ?? '', [symbol]);
  const [state, setState] = React.useState<MarketDataState | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    if (!normalizedSymbol) {
      setState(null);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [combined, benchmark] = await Promise.all([
          polygonApi.getBatchCombinedData([normalizedSymbol], 400),
          polygonApi.getHistoricalData('SPY', 400),
        ]);
        if (cancelled) return;
        const entry = combined?.[0];
        if (!entry) {
          setState(null);
          setError('Market data is currently unavailable.');
          return;
        }
        const candles = Array.isArray(entry.historicalData)
          ? [...entry.historicalData].sort((a, b) => a.timestamp - b.timestamp)
          : [];
        const benchmarkCandles = Array.isArray(benchmark)
          ? [...benchmark].sort((a, b) => a.timestamp - b.timestamp)
          : [];
        setState({ entry, candles, benchmark: benchmarkCandles });
      } catch (err) {
        if (cancelled) return;
        console.error('[MarketFooterStrip] Failed to load market data', err);
        setState(null);
        setError('Unable to load Polygon market data.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [normalizedSymbol]);

  return [state, loading, error];
};

const computeSinceArticleChange = (
  candles: HistoricalData[],
  lastPrice: number | null,
  articleTimestamp?: string | null,
) => {
  if (!articleTimestamp || !lastPrice || !candles.length) {
    return { change: null, percent: null, articlePrice: null };
  }
  const articleTime = new Date(articleTimestamp).getTime();
  if (!Number.isFinite(articleTime)) {
    return { change: null, percent: null, articlePrice: null };
  }
  const referenceCandle = candles.find((candle) => candle.timestamp >= articleTime) ?? candles[0];
  const referencePrice = referenceCandle?.close ?? referenceCandle?.open ?? null;
  if (!referencePrice || !Number.isFinite(referencePrice) || referencePrice === 0) {
    return { change: null, percent: null, articlePrice: null };
  }
  const change = lastPrice - referencePrice;
  const percent = (change / referencePrice) * 100;
  return { change, percent, articlePrice: referencePrice };
};

const computeDailyReturns = (candles: HistoricalData[]) => {
  if (!candles.length) return [] as Array<{ timestamp: number; return: number }>;
  const results: Array<{ timestamp: number; return: number }> = [];
  for (let i = 1; i < candles.length; i += 1) {
    const prev = candles[i - 1];
    const current = candles[i];
    if (!prev.close || !current.close || prev.close === 0) continue;
    const r = ((current.close - prev.close) / prev.close) * 100;
    results.push({ timestamp: current.timestamp, return: r });
  }
  return results;
};

const computeBetaAndVol = (
  assetReturns: Array<{ timestamp: number; return: number }>,
  benchmarkReturns: Array<{ timestamp: number; return: number }>,
) => {
  if (!assetReturns.length || !benchmarkReturns.length) {
    return { beta: null, realizedVol: null };
  }
  const benchmarkMap = new Map(benchmarkReturns.map((item) => [toDateKey(item.timestamp), item.return]));
  const overlapping = assetReturns.filter((item) => benchmarkMap.has(toDateKey(item.timestamp)));
  if (overlapping.length < 5) {
    return { beta: null, realizedVol: null };
  }

  const last20 = overlapping.slice(-20);
  const last60 = overlapping.slice(-60);

  const realizedVol = (() => {
    if (last20.length < 5) return null;
    const returns = last20.map((item) => item.return / 100);
    const mean = returns.reduce((acc, value) => acc + value, 0) / returns.length;
    const variance = returns.reduce((acc, value) => acc + (value - mean) ** 2, 0) / returns.length;
    return Math.sqrt(variance * 252) * 100;
  })();

  const beta = (() => {
    if (last60.length < 10) return null;
    const assetSeries = last60.map((item) => item.return / 100);
    const benchmarkSeries = last60.map((item) => (benchmarkMap.get(toDateKey(item.timestamp)) ?? 0) / 100);
    const assetMean = assetSeries.reduce((acc, value) => acc + value, 0) / assetSeries.length;
    const benchmarkMean = benchmarkSeries.reduce((acc, value) => acc + value, 0) / benchmarkSeries.length;
    const covariance = assetSeries.reduce(
      (acc, value, index) => acc + (value - assetMean) * (benchmarkSeries[index] - benchmarkMean),
      0,
    ) / assetSeries.length;
    const benchmarkVariance = benchmarkSeries.reduce((acc, value) => acc + (value - benchmarkMean) ** 2, 0) / benchmarkSeries.length;
    if (benchmarkVariance === 0) return null;
    return covariance / benchmarkVariance;
  })();

  return { beta: beta !== null && Number.isFinite(beta) ? beta : null, realizedVol: realizedVol ?? null };
};

const getPerformanceSeries = (candles: HistoricalData[], option: PerformanceKey) => {
  if (!candles.length) return [] as HistoricalData[];
  if (option === 'YTD') {
    const startOfYear = new Date();
    startOfYear.setMonth(0, 1);
    startOfYear.setHours(0, 0, 0, 0);
    return candles.filter((candle) => candle.timestamp >= startOfYear.getTime());
  }
  const lookbackDays: Record<Exclude<PerformanceKey, 'YTD'>, number> = {
    '1D': 2,
    '5D': 6,
    '1M': 22,
    '1Y': 252,
  };
  const days = lookbackDays[option] ?? 2;
  return candles.slice(-Math.max(days, 2));
};

const computeReturnFromSeries = (series: HistoricalData[]) => {
  if (!series.length) return null;
  const first = series[0];
  const last = series[series.length - 1];
  if (!first.close || !last.close || first.close === 0) return null;
  return ((last.close - first.close) / first.close) * 100;
};

const computeAlpha = (assetReturn: number | null, benchmarkSeries: HistoricalData[]) => {
  if (assetReturn === null) return null;
  const benchmarkReturn = computeReturnFromSeries(benchmarkSeries);
  if (benchmarkReturn === null) return null;
  return assetReturn - benchmarkReturn;
};

export const MarketFooterStrip: React.FC<MarketFooterStripProps> = ({
  symbol,
  profileId,
  articleTimestamp,
  onNavigateToProfile,
}) => {
  const theme = useTheme();
  const chartContainerRef = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<IChartApi | null>(null);
  const candleRef = React.useRef<ISeriesApi<'Candlestick'> | null>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [performanceOption, setPerformanceOption] = React.useState<PerformanceKey>('1D');
  const [state, loading, error] = useMarketData(symbol);

  const candles = state?.candles ?? [];
  const benchmark = state?.benchmark ?? [];
  const quote = state?.entry?.quote as StockQuote | undefined;
  const company = state?.entry?.companyInfo as CompanyInfo | undefined;
  const normalizedSymbol = React.useMemo(() => symbol?.trim().toUpperCase() ?? '', [symbol]);
  const logoSrc = React.useMemo(() => {
    if (profileId && profileId.trim().length > 0) {
      return `${getApiEndpoint()}/profile/${encodeURIComponent(profileId.trim())}/logo`;
    }
    return company?.brandingLogoUrl || company?.brandingIconUrl || undefined;
  }, [profileId, company?.brandingLogoUrl, company?.brandingIconUrl]);
  const canNavigateToProfile = React.useMemo(
    () => Boolean(onNavigateToProfile && (profileId || normalizedSymbol)),
    [onNavigateToProfile, profileId, normalizedSymbol],
  );
  const handleNavigateToProfile = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!onNavigateToProfile) return;
      onNavigateToProfile(profileId ?? null, normalizedSymbol || symbol || null);
    },
    [onNavigateToProfile, profileId, normalizedSymbol, symbol],
  );

  const lastCandle = candles.length ? candles[candles.length - 1] : undefined;
  const previousCandle = candles.length >= 2 ? candles[candles.length - 2] : undefined;
  const lastPrice = quote?.lastPrice ?? lastCandle?.close ?? previousCandle?.close ?? null;
  const previousClose = previousCandle?.close ?? lastCandle?.close ?? null;
  const change =
    lastPrice !== null && previousClose !== null && Number.isFinite(lastPrice) && Number.isFinite(previousClose) && previousClose !== 0
      ? lastPrice - previousClose
      : null;
  const changePercent =
    change !== null && previousClose !== null && previousClose !== 0
      ? (change / previousClose) * 100
      : null;

  const session = React.useMemo(() => deriveSession(), []);

  const afterHoursDelta = session !== 'regular' ? changePercent : null;

  const sparklineData = React.useMemo(() => {
    if (!candles.length) return [] as number[];
    const lookback = session === 'regular' ? 10 : 30;
    return candles
      .slice(-lookback)
      .map((candle) => candle.close)
      .filter((value) => typeof value === 'number' && Number.isFinite(value));
  }, [candles, session]);

  const volume = quote?.volume ?? lastCandle?.volume ?? null;
  const averageVolume30 = React.useMemo(() => {
    const sample = candles.slice(-30).map((candle) => candle.volume).filter((value) => typeof value === 'number' && value > 0);
    if (!sample.length) return null;
    return sample.reduce((acc, value) => acc + value, 0) / sample.length;
  }, [candles]);
  const volumeMultiple = volume !== null && averageVolume30
    ? volume / averageVolume30
    : null;

  const dayLow = lastCandle?.low ?? null;
  const dayHigh = lastCandle?.high ?? null;
  const dayPercentile =
    dayLow !== null && dayHigh !== null && dayHigh > dayLow && lastPrice !== null
      ? ((lastPrice - dayLow) / (dayHigh - dayLow)) * 100
      : null;

  const range52Week = React.useMemo(() => {
    const sample = candles.slice(-252).map((candle) => candle.close).filter((value) => typeof value === 'number' && Number.isFinite(value));
    if (!sample.length) return null as null | { min: number; max: number; percentile: number | null };
    const min = Math.min(...sample);
    const max = Math.max(...sample);
    let percentile: number | null = null;
    if (lastPrice !== null && max > min) {
      percentile = ((lastPrice - min) / (max - min)) * 100;
    }
    return { min, max, percentile };
  }, [candles, lastPrice]);

  const { change: sinceArticleChange, percent: sinceArticlePercent, articlePrice } = React.useMemo(
    () => computeSinceArticleChange(candles, lastPrice, articleTimestamp),
    [candles, lastPrice, articleTimestamp],
  );

  const dailyReturns = React.useMemo(() => computeDailyReturns(candles), [candles]);
  const benchmarkReturns = React.useMemo(() => computeDailyReturns(benchmark), [benchmark]);
  const { beta, realizedVol } = React.useMemo(() => computeBetaAndVol(dailyReturns, benchmarkReturns), [dailyReturns, benchmarkReturns]);

  const vwapDeviation =
    lastPrice !== null && lastCandle?.vwap && lastCandle.vwap !== 0
      ? ((lastPrice - lastCandle.vwap) / lastCandle.vwap) * 100
      : null;

  const performanceSeries = React.useMemo(() => getPerformanceSeries(candles, performanceOption), [candles, performanceOption]);
  const performanceReturn = React.useMemo(() => computeReturnFromSeries(performanceSeries), [performanceSeries]);
  const benchmarkPerformanceSeries = React.useMemo(
    () => getPerformanceSeries(benchmark, performanceOption),
    [benchmark, performanceOption],
  );
  const alpha = React.useMemo(
    () => computeAlpha(performanceReturn, benchmarkPerformanceSeries),
    [performanceReturn, benchmarkPerformanceSeries],
  );

  // Prepare candlestick data for lightweight-charts
  const lwcCandles = React.useMemo(() => {
    const toUtc = (t: number): UTCTimestamp => (t > 10_000_000_000 ? Math.floor(t / 1000) : t) as UTCTimestamp;
    return candles.slice(-120).map((c) => ({
      time: toUtc(c.timestamp),
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));
  }, [candles]);

  const dayRangeTooltip = dayLow !== null && dayHigh !== null && dayPercentile !== null
    ? `Day range ${formatCurrency(dayLow)} – ${formatCurrency(dayHigh)} • ${Math.round(dayPercentile)}th percentile`
    : 'Day range unavailable';

  const expandedMetrics: MetricDescriptor[] = [
    {
      label: 'Volume vs 30D Avg',
      value: volumeMultiple !== null && Number.isFinite(volumeMultiple)
        ? `${volumeMultiple >= 10 ? volumeMultiple.toFixed(0) : volumeMultiple.toFixed(1)}×`
        : '—',
      helper: volume !== null ? `${formatCompact(volume)} today` : undefined,
    },
    {
      label: 'Dollar Turnover',
      value: volume !== null && lastPrice !== null ? formatCompact(volume * lastPrice) : '—',
    },
    {
      label: 'Bid-Ask Spread',
      value: '—',
      helper: 'NBBO sampling not available',
    },
    {
      label: '% from VWAP',
      value: formatPercentRaw(vwapDeviation),
    },
    {
      label: 'Day Percentile',
      value: dayPercentile !== null ? `${Math.round(dayPercentile)}th` : '—',
      helper: dayLow !== null && dayHigh !== null ? `${formatCurrency(dayLow)} – ${formatCurrency(dayHigh)}` : undefined,
    },
    {
      label: '52W Percentile',
      value:
        range52Week?.percentile !== undefined && range52Week?.percentile !== null
          ? `${Math.round(range52Week.percentile)}th`
          : '—',
      helper:
        range52Week?.min !== undefined && range52Week?.max !== undefined
          ? `${formatCurrency(range52Week.min)} – ${formatCurrency(range52Week.max)}`
          : undefined,
    },
    {
      label: 'Market Cap',
      value: formatCompact(company?.marketCap),
    },
    {
      label: '60D Beta',
      value: beta !== null && Number.isFinite(beta) ? beta.toFixed(2) : '—',
    },
    {
      label: '20D Realized Vol',
      value: realizedVol !== null && Number.isFinite(realizedVol) ? `${realizedVol.toFixed(1)}%` : '—',
    },
  ];

  const upcomingEvents = React.useMemo(() => {
    return 'Upcoming events data unavailable';
  }, []);

  const performanceSparkline = performanceSeries.map((item) => item.close ?? 0).filter((value) => Number.isFinite(value));

  const rowDisabled = !normalizedSymbol;

  const handleToggle = React.useCallback(() => {
    if (rowDisabled) return;
    setExpanded((prev) => !prev);
  }, [rowDisabled]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (rowDisabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setExpanded((prev) => !prev);
      }
    },
    [rowDisabled],
  );

  const handlePerformanceChange = (_event: React.SyntheticEvent, value: PerformanceKey) => {
    setPerformanceOption(value);
  };

  // Create and update lightweight-charts candlestick chart
  React.useEffect(() => {
    if (!chartContainerRef.current) return;
    if (chartRef.current) return; // create once

    const chart = createChart(chartContainerRef.current, {
      autoSize: true,
      layout: {
        background: { color: theme.palette.background.default },
        textColor: theme.palette.text.secondary,
        attributionLogo: false,
      },
      watermark: { visible: false },
      grid: {
        vertLines: { color: theme.palette.divider },
        horzLines: { color: theme.palette.divider },
      },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.1, bottom: 0.1 } },
      timeScale: { borderVisible: false, rightOffset: 2 },
      crosshair: { mode: CrosshairMode.Normal },
    });

    const candlesSeries = chart.addCandlestickSeries({
      upColor: theme.palette.success.main,
      downColor: theme.palette.error.main,
      borderUpColor: theme.palette.success.main,
      borderDownColor: theme.palette.error.main,
      wickUpColor: theme.palette.success.main,
      wickDownColor: theme.palette.error.main,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    chartRef.current = chart;
    candleRef.current = candlesSeries;

    return () => {
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
    };
  }, [theme.palette.background.default, theme.palette.text.secondary, theme.palette.divider, theme.palette.success.main, theme.palette.error.main, expanded]);

  React.useEffect(() => {
    const chart = chartRef.current;
    const series = candleRef.current;
    if (!chart || !series) return;
    if (!lwcCandles.length) {
      series.setData([]);
      return;
    }
    series.setData(lwcCandles);
    chart.timeScale().fitContent();

    // Optional headline marker
    if (articleTimestamp) {
      const ts = new Date(articleTimestamp).getTime();
      if (Number.isFinite(ts)) {
        const time = (ts > 10_000_000_000 ? Math.floor(ts / 1000) : ts) as UTCTimestamp;
        series.setMarkers([
          {
            time,
            position: 'aboveBar',
            color: theme.palette.warning.main,
            shape: 'circle',
            text: 'Headline',
          },
        ]);
      } else {
        series.setMarkers([]);
      }
    } else {
      series.setMarkers([]);
    }
  }, [lwcCandles, articleTimestamp, theme.palette.warning.main, expanded]);

  const changeColor = change !== null && change < 0 ? theme.palette.error.main : theme.palette.success.main;
  const changeIcon = change !== null && change < 0 ? <TrendingDownIcon fontSize="small" /> : <TrendingUpIcon fontSize="small" />;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        flex: '1 1 100%',
        borderRadius: 2,
        overflow: 'hidden',
        borderColor: theme.palette.divider,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Stack spacing={0}>
        <ButtonBase
          component="div"
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          aria-disabled={rowDisabled}
          aria-expanded={expanded}
          role="button"
          tabIndex={rowDisabled ? -1 : 0}
          sx={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            p: 0,
            cursor: rowDisabled ? 'default' : 'pointer',
            ...(rowDisabled ? { opacity: 0.65 } : {}),
          }}
        >
          <Stack
            spacing={{ xs: 1, sm: 1.25 }}
            sx={{
              width: '100%',
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1.25, sm: 1.5 },
            }}
          >
            <Stack
              direction="row"
              spacing={{ xs: 1, sm: 1.5 }}
              alignItems="center"
              justifyContent="space-between"
              sx={{ minWidth: 0 }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <LogoImage
                  src={logoSrc}
                  alt={normalizedSymbol || 'logo'}
                  size={28}
                  paddingPx={2}
                  fallbackText={normalizedSymbol || '?'}
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: rowDisabled ? 'text.disabled' : 'primary.main',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {normalizedSymbol || 'No Ticker'}
                </Typography>
                {canNavigateToProfile ? (
                  <Tooltip title="Open profile" enterDelay={300}>
                    <IconButton
                      size="small"
                      onClick={handleNavigateToProfile}
                      aria-label="Open profile"
                      sx={{
                        color: rowDisabled ? 'text.disabled' : 'primary.main',
                        '&:hover': { color: rowDisabled ? 'text.disabled' : 'primary.light' },
                      }}
                    >
                      <OpenInNewIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: { xs: 'none', md: 'inline' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {company?.name ? `• ${company.name}` : '• Company unavailable'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                {session === 'regular' ? (
                  <Chip size="small" label="Market open" color="success" variant="outlined" />
                ) : session === 'pre-market' ? (
                  <Chip size="small" label="Pre-market" color="warning" variant="outlined" />
                ) : (
                  <Chip size="small" label="After-hours" color="info" variant="outlined" />
                )}
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 1.25, md: 2 }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {formatCurrency(lastPrice)}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {change !== null ? changeIcon : null}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: change !== null ? changeColor : theme.palette.text.secondary,
                      }}
                    >
                      {change !== null && changePercent !== null
                        ? `${change >= 0 ? '+' : ''}${change.toFixed(2)} (${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%)`
                        : '—'}
                    </Typography>
                  </Stack>
                  {afterHoursDelta !== null ? (
                    <Tooltip title="Δ% is vs. previous regular close, not pre/post">
                      <Typography variant="caption" color="text.secondary">
                        AH {afterHoursDelta >= 0 ? '+' : ''}{afterHoursDelta.toFixed(2)}%
                      </Typography>
                    </Tooltip>
                  ) : null}
                </Stack>
              </Stack>

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                sx={{ flex: 1, width: '100%' }}
              >
                {loading ? (
                  <Box sx={{ width: 160, maxWidth: '100%' }}>
                    <LinearProgress color="primary" />
                  </Box>
                ) : sparklineData.length ? (
                  <Tooltip title={dayRangeTooltip} placement="top">
                    <Box sx={{ width: { xs: 160, md: 200 } }}>
                      <Sparkline
                        data={sparklineData}
                        color={change !== null && change < 0 ? theme.palette.error.main : theme.palette.success.main}
                        width="100%"
                        height={32}
                      />
                    </Box>
                  </Tooltip>
                ) : null}
                <Tooltip title="Volume relative to trailing 30 trading days">
                  <Chip
                    size="small"
                    icon={<InfoOutlinedIcon fontSize="inherit" />}
                    label={
                      volumeMultiple !== null && Number.isFinite(volumeMultiple)
                        ? `${volumeMultiple >= 10 ? volumeMultiple.toFixed(0) : volumeMultiple.toFixed(1)}× vol`
                        : '—'
                    }
                    variant="outlined"
                  />
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggle();
                  }}
                  aria-label={expanded ? 'Collapse market overview' : 'Expand market overview'}
                  sx={{ ml: { xs: 0, md: 0.5 } }}
                >
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </ButtonBase>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ borderColor: theme.palette.divider, opacity: 0.6 }} />
          <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 2, sm: 2.5 }, bgcolor: theme.palette.background.default }}>
            {loading ? (
              <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  Loading market overview…
                </Typography>
              </Stack>
            ) : error ? (
              <Typography variant="body2" color="error.main">
                {error}
              </Typography>
            ) : !state ? (
              <Typography variant="body2" color="text.secondary">
                Market overview unavailable for this report.
              </Typography>
            ) : (
              <Stack spacing={3}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 2 }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Stack spacing={0.25}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {formatCurrency(lastPrice)}
                      <Typography component="span" variant="subtitle2" color="text.secondary" sx={{ ml: 1 }}>
                        {change !== null && changePercent !== null
                          ? `${change >= 0 ? '+' : ''}${change.toFixed(2)} (${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%)`
                          : '—'}
                      </Typography>
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title="Move since article publish timestamp">
                        <Chip
                          size="small"
                          color={sinceArticleChange !== null && sinceArticleChange < 0 ? 'error' : 'success'}
                          label={
                            sinceArticleChange !== null && sinceArticlePercent !== null && articlePrice !== null
                              ? `${sinceArticlePercent >= 0 ? '+' : ''}${sinceArticlePercent.toFixed(2)}% since headline`
                              : 'No change data'
                          }
                        />
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary">
                        Snapshot latency: Polygon real-time (may be delayed up to 15 minutes)
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {session === 'regular' ? (
                      <Chip size="small" label="Market open" color="success" variant="outlined" />
                    ) : session === 'pre-market' ? (
                      <Chip size="small" label="Pre-market" color="warning" variant="outlined" />
                    ) : (
                      <Chip size="small" label="After-hours" color="info" variant="outlined" />
                    )}
                  </Stack>
                </Stack>

                <Box sx={{ height: { xs: 240, md: 300 }, width: '100%', position: 'relative' }}>
                  <Box ref={chartContainerRef} sx={{ position: 'absolute', inset: 0 }} />
                  {!lwcCandles.length && (
                    <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}>
                      <Typography variant="body2" color="text.secondary">
                        Chart data unavailable.
                      </Typography>
                    </Stack>
                  )}
                </Box>

                <Box>
                  <Grid container spacing={2} columns={{ xs: 2, sm: 8, md: 12 }}>
                    {expandedMetrics.map((metric) => (
                      <Grid item xs={2} sm={4} md={3} key={metric.label}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                            {metric.label}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {metric.value}
                          </Typography>
                          {metric.helper ? (
                            <Typography variant="caption" color="text.secondary">
                              {metric.helper}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Tabs
                      value={performanceOption}
                      onChange={handlePerformanceChange}
                      aria-label="Performance horizon selector"
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      {PERFORMANCE_OPTIONS.map((option) => (
                        <Tab key={option.id} label={option.label} value={option.id} />
                      ))}
                    </Tabs>
                    <Stack spacing={0.25} sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Total return {formatPercentRaw(performanceReturn)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        vs SPY: {alpha !== null && Number.isFinite(alpha) ? `${alpha >= 0 ? '+' : ''}${alpha.toFixed(2)}%` : '—'}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                    <Box sx={{ flex: 1, width: '100%' }}>
                      {performanceSparkline.length ? (
                        <Sparkline
                          data={performanceSparkline}
                          color={performanceReturn !== null && performanceReturn < 0 ? theme.palette.error.main : theme.palette.success.main}
                          width="100%"
                          height={48}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Performance sparkline unavailable.
                        </Typography>
                      )}
                    </Box>
                    <Stack spacing={0.25}>
                      <Typography variant="caption" color="text.secondary">
                        Showing {performanceSeries.length ? `${formatInteger(performanceSeries.length)} sessions` : 'no sessions'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Benchmark data sourced from SPY daily closes
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Upcoming events
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {upcomingEvents}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>
        </Collapse>
      </Stack>
    </Paper>
  );
};

export default MarketFooterStrip;
